import {CSSProperties, styled} from '@linaria/react';
import React, {PropsWithChildren} from 'react';
import {COLORS} from '../../../constants/colors';
import {BORDER, RADIUS, SIZE, SPACING} from '../../../constants/sizing';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export type OpenerType = 'single-arrow' | 'double-arrow' | 'caret' | null;

interface Props {
  isOpen: boolean;
  isActive?: boolean;
  openerType?: OpenerType;
  onClick?: () => void;
  forceBlackBorderHover?: boolean;
  forceGreyBorder?: boolean;
  forceIconColor?: keyof typeof COLORS;
  className?: string;
  style?: CSSProperties;
  disableFocusOnOpenerButton?: boolean;
}
export const ContentOpener = ({
  children,
  isOpen,
  isActive,
  onClick,
  openerType,
  forceBlackBorderHover,
  forceGreyBorder,
  forceIconColor,
  disableFocusOnOpenerButton,
  ref,
  ...rest
}: PropsWithChildren<Props> & {ref?: React.Ref<HTMLButtonElement>}) => {
  return (
    <ContentOpenerWrapper
      $isOpen={isOpen}
      $isActive={isActive}
      $forceGreyBorder={forceGreyBorder}
      data-black-border-on-hover={forceBlackBorderHover}
      onClickCapture={() => onClick?.()}
      data-testid="opener"
      role="button"
      aria-expanded={isOpen}
      tabIndex={disableFocusOnOpenerButton ? -1 : undefined}
      {...rest}
      ref={ref}
    >
      {children}
      {openerType && (
        <OpenerArrow
          openerType={openerType}
          isOpen={isOpen}
          forceIconColor={forceIconColor}
        />
      )}
    </ContentOpenerWrapper>
  );
};

const OpenerArrow: React.FC<{
  openerType: OpenerType;
  isOpen: boolean;
  forceIconColor?: keyof typeof COLORS;
}> = ({openerType, isOpen, forceIconColor}) => {
  switch (openerType) {
    case 'single-arrow':
      return (
        <SingleArrow
          asset={GenericIconName.Dropdown}
          fill={COLORS['Fill.Black']}
          $isOpen={isOpen}
          $forceIconColor={forceIconColor}
        />
      );
    case 'caret':
      return (
        <SingleArrow
          asset={GenericIconName.ArrowDown}
          fill={COLORS['Fill.Black']}
          $isOpen={isOpen}
          $forceIconColor={forceIconColor}
        />
      );
    case 'double-arrow':
    default:
      return (
        <DoubleArrow
          size={'XS'}
          asset={GenericIconName.DoubleArrow}
          $isOpen={isOpen}
          fill={forceIconColor}
        />
      );
  }
};

const StyledIcon = styled(Icon)<{
  $isOpen: boolean;
  $forceIconColor?: keyof typeof COLORS;
}>`
  display: inline;

  color: ${({$forceIconColor}) =>
    $forceIconColor ? `${COLORS[$forceIconColor]}` : 'unset'};
`;

export const ContentOpenerWrapper = styled.button<{
  $isOpen: boolean;
  $isActive?: boolean;
  $forceGreyBorder?: boolean;
  $blackBorderOnHover?: boolean;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  border: solid ${BORDER.Sm};
  border-color: ${({$isActive, $forceGreyBorder}) => {
    if ($forceGreyBorder) {
      return COLORS.Grey3;
    }
    return $isActive ? COLORS.Black : COLORS.Grey3;
  }};
  color: ${({$isOpen, $isActive}) =>
    $isActive || $isOpen ? COLORS.Black : COLORS.Grey1};
  background-color: ${COLORS.White};
  border-radius: ${RADIUS.Sm};
  position: relative;
  padding: 0;
  width: 100%;
  padding: ${SPACING['Y.Sm']} ${SPACING['X.Md']} ${SPACING['X.Sm']};
  gap: ${SPACING['X.Md']};

  &:hover {
    color: ${COLORS.Black};

    ${StyledIcon} {
      opacity: 1;
    }
  }

  &[data-black-border-on-hover='true'] {
    &:hover {
      border: solid ${BORDER.Sm} ${COLORS.Black};
    }
  }

  justify-content: space-between;
`;

const SingleArrow = styled(StyledIcon)<{$isOpen: boolean; $isActive?: boolean}>`
  transform: ${({$isOpen}) => ($isOpen ? 'scale(-1);' : 'unset')};
  height: ${SIZE.Lg};
  width: ${SIZE.Lg};
`;

const DoubleArrow = styled(StyledIcon)`
  margin-top: ${SPACING['Y.Xs']};
`;
